import React, { useState, useRef } from 'react';
import { Client } from '../../../entities/client';
import { createTicket, sendMessage } from '../../../entities/ticket/api/ticketApi';
import { TicketSite } from '../../../entities/ticket';
import * as S from './SendEmailModal.styles';

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onTicketCreated?: (ticketId: string) => void;
}

export const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onClose,
  client,
  onTicketCreated,
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setAttachments([...attachments, ...files]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      alert('Введите сообщение');
      return;
    }

    if (!client.mainContact?.email) {
      alert('У клиента нет email адреса');
      return;
    }

    try {
      setSending(true);
      
      const site = (client.externalSite as TicketSite) || 'unknown';
      
      const ticket = await createTicket({
        title: `Сообщение для ${client.name}`,
        site,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.mainContact.email,
        priority: 'medium',
      });

      if (!ticket || !ticket.id) {
        throw new Error('Не удалось создать тикет');
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      await sendMessage(
        ticket.id, 
        message, 
        attachments.length > 0 ? attachments : undefined, 
        false
      );

      handleClose();
      if (onTicketCreated) {
        onTicketCreated(ticket.id);
      }
    } catch (error: any) {
      console.error('Ошибка отправки email:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Не удалось отправить сообщение';
      alert(`Ошибка: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={handleClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>Отправить email</S.ModalTitle>
          <S.CloseButton onClick={handleClose}>×</S.CloseButton>
        </S.ModalHeader>

        <form onSubmit={handleSubmit}>
          <S.ModalBody>
            <S.RecipientInfo>
              <S.RecipientLabel>Получатель:</S.RecipientLabel>
              <S.RecipientValue>{client.mainContact?.email}</S.RecipientValue>
            </S.RecipientInfo>

            <S.FormGroup>
              <S.Label>Сообщение *</S.Label>
              <S.Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Введите сообщение..."
                rows={8}
                required
              />
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Вложения</S.Label>
              <S.FileInputWrapper>
                <S.FileInput
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                />
                <S.FileInputButton type="button" onClick={() => fileInputRef.current?.click()}>
                  Выбрать файлы
                </S.FileInputButton>
              </S.FileInputWrapper>
              {attachments.length > 0 && (
                <S.AttachmentsList>
                  {attachments.map((file, index) => (
                    <S.AttachmentItem key={index}>
                      <S.AttachmentName>{file.name}</S.AttachmentName>
                      <S.RemoveButton type="button" onClick={() => removeAttachment(index)}>
                        ×
                      </S.RemoveButton>
                    </S.AttachmentItem>
                  ))}
                </S.AttachmentsList>
              )}
            </S.FormGroup>
          </S.ModalBody>

          <S.ModalFooter>
            <S.Button type="button" onClick={handleClose}>
              Отмена
            </S.Button>
            <S.Button type="submit" $variant="primary" disabled={sending}>
              {sending ? 'Отправка...' : 'Отправить'}
            </S.Button>
          </S.ModalFooter>
        </form>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

